import { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const navItems = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: 'fa-th-large' },
  { path: '/admin/profile', label: 'My Profile', icon: 'fa-user' },
  { path: '/admin/students', label: 'Students', icon: 'fa-users' },
  { path: '/admin/results', label: 'Results', icon: 'fa-award' },
  { path: '/admin/attendance', label: 'Attendance', icon: 'fa-clipboard-check' },
  { path: '/admin/subjects', label: 'Subjects', icon: 'fa-book' },
  { path: '/admin/notifications', label: 'Notifications', icon: 'fa-bell' },
];

const StaffLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileDropdown, setProfileDropdown] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();

  const admin = user?.user;

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <i className="fas fa-lock text-4xl text-slate-300 mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Access Denied</h2>
          <p className="text-slate-500 mb-4">Please login as admin to continue.</p>
          <button
            onClick={() => navigate('/admin/login')}
            className="px-6 py-2.5 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-72 bg-white border-r border-slate-200 transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } flex flex-col`}
      >
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-indigo-800 rounded-xl flex items-center justify-center text-white shadow-lg">
              <i className="fas fa-user-shield text-lg" />
            </div>
            <div>
              <h1 className="font-bold text-slate-800 text-sm leading-tight">Islamic School</h1>
              <p className="text-xs text-slate-400">Admin Portal</p>
            </div>
          </div>
        </div>

        <div className="p-4 mx-4 mt-4 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl border border-indigo-100">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-indigo-200 rounded-full flex items-center justify-center text-indigo-700 font-bold text-lg">
              {admin?.name?.charAt(0) || 'A'}
            </div>
            <div className="overflow-hidden">
              <p className="font-semibold text-slate-800 text-sm truncate">{admin?.name || 'Administrator'}</p>
              <p className="text-xs text-slate-500">{admin?.role || 'Admin'}</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-indigo-600 text-white shadow-md shadow-indigo-200'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <i className={`fas ${item.icon} w-5 text-center ${isActive ? 'text-white' : 'text-slate-400'}`} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-100">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 transition-all w-full"
          >
            <i className="fas fa-sign-out-alt w-5 text-center" />
            Logout
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-slate-200 px-4 lg:px-8 py-4 sticky top-0 z-30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-slate-100 text-slate-600"
              >
                <i className="fas fa-bars text-lg" />
              </button>
              <div>
                <h2 className="text-lg font-bold text-slate-800">
                  {navItems.find((n) => n.path === location.pathname)?.label || 'Dashboard'}
                </h2>
                <p className="text-xs text-slate-500 hidden sm:block">
                  {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Link
                to="/admin/notifications"
                className="relative p-2.5 rounded-xl hover:bg-slate-100 text-slate-600 transition-colors"
              >
                <i className="fas fa-bell text-lg" />
              </Link>

              <div className="relative">
                <button
                  onClick={() => setProfileDropdown(!profileDropdown)}
                  className="flex items-center gap-3 pl-3 pr-4 py-2 rounded-xl hover:bg-slate-100 transition-colors"
                >
                  <div className="w-9 h-9 bg-gradient-to-br from-indigo-500 to-indigo-700 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {admin?.name?.charAt(0) || 'A'}
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-semibold text-slate-800 leading-tight">{admin?.name || 'Administrator'}</p>
                    <p className="text-xs text-slate-500">{admin?.role || 'Admin'}</p>
                  </div>
                  <i className={`fas fa-chevron-down text-xs text-slate-400 hidden sm:block transition-transform ${profileDropdown ? 'rotate-180' : ''}`} />
                </button>

                {profileDropdown && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setProfileDropdown(false)} />
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-xl border border-slate-100 z-50 py-2">
                      <div className="px-4 py-3 border-b border-slate-100">
                        <p className="text-sm font-semibold text-slate-800">{admin?.name || 'Administrator'}</p>
                        <p className="text-xs text-slate-500">{admin?.email || ''}</p>
                      </div>
                      <Link to="/admin/profile" onClick={() => setProfileDropdown(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                        <i className="fas fa-user text-slate-400 w-5 text-center" /> My Profile
                      </Link>
                      <Link to="/admin/dashboard" onClick={() => setProfileDropdown(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                        <i className="fas fa-th-large text-slate-400 w-5 text-center" /> Dashboard
                      </Link>
                      <div className="border-t border-slate-100 mt-2 pt-2">
                        <button onClick={() => { setProfileDropdown(false); handleLogout(); }} className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors w-full">
                          <i className="fas fa-sign-out-alt text-red-400 w-5 text-center" /> Logout
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default StaffLayout;
